import { EnvSchemaTypes, env } from './env';

export enum Routes {
  Account = '/account',
  ApiKeys = '/api-keys',
  Payment = '/payment',
  Catalog = '/catalog',
  Dashboard = '/dashboard',
  Requests = '/requests',
  Graphql = '/graphql',
  Resources = '/resources',
  Affiliate = '/affiliate-disclosure',
  Login = '/login',
  Register = '/register',
  Reset = '/reset',
  Orders = '/orders',
  CreateOrder = '/orders/new',
  Billing = '/billing',
  Team = '/team',
  Commissions = '/commissions',
  Commission = '/commissions/:id',
  Merchants = '/merchants',
  Order = '/orders/:orderId',
  CheckoutIntents = '/checkout-intents',
  DrawdownSetup = '/drawdown-setup',
  NoAccess = '/no-access',
  Home = '/',
  All = '*',
}

export interface PromoNotification {
  id: string;
  title: string;
  link: string;
  startedAt: number;
  finishedBy: number;
  displayCount: number;
}

const gqlEndpoints: Record<EnvSchemaTypes['VITE_NODE_ENV'], string> = {
  development: 'http://localhost:3000/v1/query',
  staging: 'https://graphql.commerce.staging.jolly.dev/v1/query',
  production: 'https://graphql.commerce.jolly.dev/v1/query',
};

const adminEndpoints: Record<EnvSchemaTypes['VITE_NODE_ENV'], string> = {
  development: 'http://localhost:3095/v1/query',
  staging: 'https://admin-api-l46hfxmk6q-uc.a.run.app/v1/query',
  production: 'https://admin-api-ggymj6kjkq-uc.a.run.app/v1/query',
};

const checkoutIntentEndpoints: Record<EnvSchemaTypes['VITE_NODE_ENV'], string> = {
  development: 'http://localhost:6018/api/v1/checkout-intents',
  staging: 'https://api.commerce.staging.jolly.dev/api/v1/checkout-intents',
  production: 'https://api.commerce.jolly.dev/api/v1/checkout-intents',
};

export const baseURL = {
  development: 'http://localhost:6018',
  staging: 'https://api.commerce.staging.jolly.dev',
  production: 'https://api.commerce.jolly.dev',
};

const config = {
  external: {
    gql: gqlEndpoints[env.VITE_NODE_ENV],
    adminGql: adminEndpoints[env.VITE_NODE_ENV],
    checkoutIntent: checkoutIntentEndpoints[env.VITE_NODE_ENV],
    doc: {
      home: 'https://docs-v1.rye.com',
      homeV2: 'https://docs.rye.com',
      tutorial: 'https://tutorial.rye.com',
      getStartedDocs: 'https://docs-v1.rye.com/get-started/adding-products/overview',
      demo: 'https://search.rye.com',
      webhookUpdate: 'https://docs-v1.rye.com/webhook-updates',
      webhooksV2: 'https://docs.rye.com/webhooks',
      quickStart: 'https://docs-v1.rye.com/quick-start',
      quickStartV2: 'https://docs.rye.com/api-v2/example-flows/simple-checkout',
      backendOrdering: 'https://docs-v1.rye.com/placing-orders-directly-from-the-backend',
      configureAmazonGuide: 'https://docs-v1.rye.com/amazon-business',
      jwtDocs: 'https://docs-v1.rye.com/jwt-authentication',
    },
    terms: 'https://rye.com/terms-of-service',
    privacyPolicy: 'https://rye.com/privacy-policy',
    ipService: 'https://api.ipify.org',
  },
  social: {
    twitter: 'https://twitter.com/rye',
    github: 'https://github.com/rye-com',
    linkedin: 'https://www.linkedin.com/company/rye/',
  },
  routesMap: [
    { title: 'Account', path: Routes.Account },
    { title: 'API Keys', path: Routes.ApiKeys },
    { title: 'Requests', path: Routes.Requests },
    { title: 'GraphQL', path: Routes.Graphql },
    { title: 'Developer Resources', path: Routes.Resources },
    { title: 'GraphQL Orders', path: Routes.Orders },
    { title: 'Checkout Intents', path: Routes.CheckoutIntents },
    { title: 'Catalog', path: Routes.Catalog },
    { title: 'GraphQL Order details', path: Routes.Order },
    { title: 'Dashboard', path: Routes.Dashboard },
    { title: 'Billing', path: Routes.Billing },
    { title: 'Team', path: Routes.Team },
    { title: 'Commissions', path: Routes.Commissions },
    { title: 'Merchants', path: Routes.Merchants },
    { title: 'No Access', path: Routes.NoAccess },

    { title: 'Affiliate disclosure', path: Routes.Affiliate },

    { title: 'Login', path: Routes.Login },
    { title: 'Register', path: Routes.Register },
    { title: 'Reset', path: Routes.Reset },

    { title: 'Home', path: Routes.Home },
    { title: 'Error', path: Routes.All },
  ],
  routeDefaultHome: Routes.Dashboard,
  routesNav: [
    Routes.Dashboard,
    Routes.Graphql,
    Routes.Account,
    Routes.Requests,
    Routes.Resources,
    Routes.Orders,
    Routes.CheckoutIntents,
    Routes.Merchants,
    Routes.Commissions,
    Routes.Billing,
    Routes.Team,
  ],
  promoNotifications: [
    {
      id: 'wehbooks',
      title: 'Please fill up Webhook URL!',
      link: '/account',
      displayCount: 0,
    },
  ] as PromoNotification[],
};

export enum IFRAME_HEADERS {
  AUTHORIZATION = 'Authorization',
  RYE_SHOPPER_IP = 'Rye-Shopper-IP',
  RYE_DEBUG = 'Rye-Debug',
}

// The VITE_NODE_ENV set at build time, with first letter capitalized
export const ENV_TITLE_CASE =
  env.VITE_NODE_ENV.charAt(0).toUpperCase() + env.VITE_NODE_ENV.slice(1);

// Stripe customer portal login URL
// Docs: https://docs.stripe.com/customer-management/activate-no-code-customer-portal
export const getStripePortalUrl = () =>
  env.VITE_NODE_ENV === 'production'
    ? 'https://pay.jolly.com/p/login/eVa8zugvyh31dOwdQQ'
    : 'https://billing.stripe.com/p/login/test_bIY14M8GB5iB1zicMM';

export const getSwitchLink = () => {
  switch (env.VITE_NODE_ENV) {
    case 'staging':
    case 'development':
      return 'https://console.rye.com';
    default:
      return 'https://staging.console.rye.com';
  }
};

export default config;
